<script setup>
import Titre from "@/components/atoms/titre";
import Icon from "@/components/atoms/icon";
import Divider from "@/components/atoms/divider";
import Button from "@/components/atoms/button";
import Popin from "@/containers/popin-adaptive";
import LazyImage from "@/components/atoms/lazy-image";
import Badge from "@/components/atoms/badge";
import Alert from "@/components/molecules/alert";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormRadio from "@/components/molecules/form-radio";

import consignationDesSacs from "@/assets/images/consignation-des-sacs.svg";

const emit = defineEmits(["setChoixSacs"]);
const selectedChoixSacsDrive = ref(null);

const setChoixSacs = (value) => {
  selectedChoixSacsDrive.value = value;
  emit("setChoixSacs", value);
};

const props = defineProps({
  isDrive: {
    type: Boolean,
    required: false,
    default: false
  },
  isOffert: {
    type: Boolean,
    required: false,
    default: false
  },
  consigneSacs: {
    type: Boolean,
    required: false,
    default: false
  },
  montantSac: {
    type: Number,
    required: false,
    default: 0
  },
  isError: {
    type: Boolean,
    required: false,
    default: false
  }
});
</script>

<template>
  <div
    :class="[isDrive && 'u-mr-md@xs u-ml-md@xs']"
    class="u-flex u-flex-column"
  >
    <div class="u-flex u-flex-align-center u-mb-xs25">
      <Icon
        name="sac-consignes"
        variant="primary"
      />
      <Titre
        tag="h2"
        size="xs"
        class="u-ml-xs"
      >
        {{ isDrive ? 'Besoin de sacs ?' : 'Sacs consignés' }}
      </Titre>
      <Divider
        class="u-flex-item-fluid u-ml-xs u-none@md"
        dotted
      />
    </div>

    <Form
      v-if="isDrive"
      class="u-flex u-flex-column u-gap-sm u-mb-sm"
    >
      <FormGroup size="full">
        <FormRadio
          :state="isError ? 'error' : '' "
          block
          name="radio"
          :value="false"
          @change="setChoixSacs(false)"
        >
          <div class="u-flex u-flex-justify-between u-flex-column@xs u-flex-align-center u-flex-align-start@xs u-gap-xs u-full-width u-font-14 u-text-left">
            <p>J'apporte mes propres sacs que je remplirai moi-même</p>
            <p
              v-if="consigneSacs"
              class="u-color-primary u-font-18"
            >
              Gratuit
            </p>
          </div>
        </FormRadio>
      </FormGroup>

      <FormGroup size="full">
        <FormRadio
          :state="isError ? 'error' : '' "
          block
          name="radio"
          :value="true"
          @change="setChoixSacs(true)"
        >
          <div class="u-flex u-flex-justify-between u-flex-column@xs u-flex-align-center u-flex-align-start@xs u-gap-xs u-full-width u-font-14 u-text-left">
            <p
              v-if="consigneSacs"
              class="u-text-left"
            >
              Je souhaite que mon magasin me fournisse des sacs consignés à {{ formatPrix(montantSac) }} € / sac. <br>
              {{ isOffert ? "Pour la prochaine commande, une " : "Une " }} pré autorisation bancaire sera demandée cependant vous ne paierez que les sacs utilisés pour vos courses. Le nombre de sacs peut varier en fonction du volume de votre panier.
            </p>
            <p
              v-else
              class="u-text-left"
            >
              Je souhaite que mon magasin me fournisse des sacs
            </p>

            <template v-if="consigneSacs">
              <p
                v-if="isOffert"
                class="u-color-primary u-font-18 u-text-right u-flex-shrink-none u-text-left@xs"
              >
                Offert pour cette <br> commande
              </p>
              <p
                v-else
                class="u-color-primary u-font-18 u-text-right"
              >
                {{ formatPrix(montantSac) }}&nbsp;€/sac
              </p>
            </template>
          </div>
        </FormRadio>
      </FormGroup>
    </Form>

    <div v-else>
      <div class="u-flex u-flex-justify-between u-flex-column@xs u-flex-align-start@xs u-gap-xs u-full-width u-font-14 u-mb-xs25 u-text-left">
        <p class="u-text-left">
          Vos courses sont livrées dans des sacs consignés à {{ formatPrix(montantSac) }} € / sac. <br>
          {{ isOffert ? "Pour la prochaine commande, une " : "Une " }} pré autorisation bancaire sera demandée cependant vous ne paierez que les sacs utilisés pour vos courses. Le nombre de sacs peut varier en fonction du volume de votre panier.
        </p>
        <p class="u-color-primary u-font-18 u-text-right u-flex-shrink-none u-text-left@xs">
          <span v-if="isOffert"> Offert pour cette <br> commande</span>
          <span v-else>{{ formatPrix(montantSac) }}&nbsp;€/sac</span>
        </p>
      </div>
    </div>

    <Button
      v-if="consigneSacs"
      link
      variant="primary"
      class="u-flex-self-start u-mb-md"
      @click="$refs.enSavoirPlus.show()"
    >
      En savoir plus sur les sacs consignés
    </Button>
    <Popin
      ref="enSavoirPlus"
      size="lg"
    >
      <div class="u-flex u-flex-column u-gap-md u-flex-align-center u-pb-md u-pb-md@xs">
        <LazyImage :src="consignationDesSacs" />
        <Titre
          tag="h2"
          size="md"
          class="u-text-center"
        >
          La consigne de sacs, expliquée en 3 étapes
        </Titre>

        <div class="u-flex u-flex-column u-gap-md">
          <div class="u-flex u-pl-xs25 u-pr-xs25">
            <Badge
              size="lg"
              class="u-color-white u-bgcolor-primary u-mr-xs25 u-p-xs25 u-flex-shrink-none"
              circle
            >
              1
            </Badge>
            <div class="u-mt-xxs">
              <p class="u-font-secondary u-font-18 u-font-700 u-mb-xs">
                Lors de ma commande
              </p>
              <p class="u-mb-xs25 u-font-15">
                Mon magasin me fournit des sacs consignés à {{ formatPrix(montantSac) }} € / sac. <br>
                Une pré autorisation bancaire sera demandée au moment du paiement cependant vous ne paierez que les sacs utilisés pour vos courses. <br>
                Le nombre de sacs peut varier en fonction du volume de votre panier.
              </p>

              <Alert
                v-if="isOffert"
                has-icon
                icon-name="info-circle-outline"
                icon-size="sm"
                variant="primary-lighter"
              >
                Les sacs vous sont offerts pour cette commande.
              </Alert>
            </div>
          </div>
          <div class="u-flex u-pl-xs25 u-pr-xs25">
            <Badge
              size="lg"
              class="u-color-white u-bgcolor-primary u-mr-xs25 u-p-xs25 u-flex-shrink-none"
              circle
            >
              2
            </Badge>
            <div class="u-mt-xxs">
              <p class="u-font-secondary u-font-18 u-font-700 u-mb-xs">
                Pour mes commandes suivantes
              </p>
              <p
                v-if="isDrive"
                class="u-font-15"
              >
                Je peux venir avec mes sacs consignés pour y ranger mes achats ou me faire rembourser {{ formatPrix(montantSac) }} € par sac rapporté. <br> Les sacs sont acceptés tels qu’ils sont, même tachés et abîmés, dans la limite de 10 sacs par passage au drive.
              </p>
              <p
                v-else
                class="u-font-15"
              >
                Je peux rendre mes sacs consignés au livreur pour me faire rembourser {{ formatPrix(montantSac) }} € par sac retourné, dans la limite de 10 sacs par retour. Les sacs sont acceptés tels qu’ils sont, même tachés et abîmés. <br> Je peux également les utiliser pour mes prochaines commandes drive.
              </p>
            </div>
          </div>
          <div class="u-flex u-pl-xs25 u-pr-xs25">
            <Badge
              size="lg"
              class="u-color-white u-bgcolor-primary u-mr-xs25 u-p-xs25 u-flex-shrink-none"
              circle
            >
              3
            </Badge>
            <div class="u-mt-xxs">
              <p class="u-font-secondary u-font-18 u-font-700 u-mb-xs">
                Mes sacs sont recyclés
              </p>
              <p class="u-font-15">
                Grâce aux sacs consignés, nous pouvons réutiliser les emballages et réduire ensemble notre impact sur l’environnement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Popin>
  </div>
</template>
<style lang="scss" scoped></style>
